function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Export data to specified format
 * @param app - Qlik app instance
 * @param id - Qlik object ID (for charts or containers)
 * @param format - OOXML, CSV_C, CSV_T
 * @param state - "A" (all values), "P" (possible values)
 * @param onExportStart - callback when export begins
 * @param onExportFinish - callback when export ends (link, error)
 */
export async function ExportData({
  app,
  id,
  format = "OOXML",
  state = "P",
  onExportStart = null,
  onExportFinish = null
}) {
  const exportType = "data";
  let vis = null;

  try {
    // Get visualization for the provided id
    vis = await app.visualization.get(id);

    // Always prefer fresh layout (avoids stale/missing vis.model.layout)
    let layout = null;
    try {
      layout = await vis.model.getLayout();
    } catch (e) {
      // If layout can't be fetched, proceed with current vis (export may still work)
      console.warn("ExportData: failed to get layout for id:", id, e);
    }

    // Resolve container → active child visualization
    if (layout?.qInfo?.qType) {
      const qType = layout.qInfo.qType;

      if (qType === "container") {
        // For container, pick activeTab or defaultTab (these are real object ids)
        const selectTab = layout.activeTab || layout.defaultTab;
        if (selectTab) {
          vis.close();
          vis = await app.visualization.get(selectTab);
        }
      } else if (qType === "sn-tabbed-container") {
        // For tabbed container, map cached/default tab (childRefId) to real object id (qId)
        const selectTab = layout.cachedTabId || layout.defaultTabId;
        const childItems = layout.qChildList?.qItems || [];
        const child = childItems.find(item => item?.qData?.childRefId === selectTab);
        const realId = child?.qInfo?.qId || selectTab;

        if (realId) {
          vis.close();
          vis = await app.visualization.get(realId);
        }
      }
      // Note: sn-layout-container handled by React component, not here
    }

    // Invoke start callback (never allow it to create an unhandled rejection)
    if (onExportStart) {
      try {
        await onExportStart({ vis, exportType });
      } catch (cbErr) {
        console.error("ExportData: onExportStart threw:", cbErr);
      }
    }

    // Perform export (await it so callers can catch)
    const link = await vis.exportData({ format, state });

    // Finish callback or open
    if (onExportFinish) {
      try {
        await onExportFinish({ vis, link, error: null, exportType });
      } catch (cbErr) {
        console.error("ExportData: onExportFinish threw:", cbErr);
      }
    } else {
      window.open(link, "_blank");
    }

    return link;
  } catch (error) {
    // Report error to finish callback, but never let callback errors become unhandled
    if (onExportFinish) {
      try {
        await onExportFinish({ vis: null, link: null, error, exportType });
      } catch (cbErr) {
        console.error("ExportData: onExportFinish (error path) threw:", cbErr);
      }
    }
    throw error;
  } finally {
    try {
      vis && vis.close();
    } catch (e) {
      // ignore
    }
  }
}

/**
 * Export image
 * @param app - qlik app
 * @param id - qlik object id
 * @param width - width in pixels
 * @param height - height in pixels
 * @param format - png, jpg
 */
export async function ExportImg({
  app,
  id,
  width = 1920,
  height = 1080,
  format = "jpg",
  onExportStart = null,
  onExportFinish = null
}) {
  const exportType = "image";
  let vis = null;

  try {
    vis = await app.visualization.get(id);
    const settings = { format, height, width };

    if (onExportStart) {
      try {
        await onExportStart({ vis, settings, exportType });
      } catch (cbErr) {
        console.error("ExportImg: onExportStart threw:", cbErr);
      }
    }

    const link = await vis.exportImg(settings);

    if (onExportFinish) {
      try {
        await onExportFinish({ vis, link, error: null, exportType });
      } catch (cbErr) {
        console.error("ExportImg: onExportFinish threw:", cbErr);
      }
    } else {
      window.open(link, "_blank");
    }

    return link;
  } catch (error) {
    if (onExportFinish) {
      try {
        await onExportFinish({ vis: null, link: null, error, exportType });
      } catch (cbErr) {
        console.error("ExportImg: onExportFinish (error path) threw:", cbErr);
      }
    }
    throw error;
  } finally {
    try {
      vis && vis.close();
    } catch (e) {
      // ignore
    }
  }
}

/**
 * Export PDF
 * @param app - qlik app
 * @param id - qlik object id
 * @param documentSize - Page size of the output PDF: a3, a4, a5, a6, a7
 * @param orientation - portrait, landscape
 * @param aspectRatio - 0, 2
 */
export async function ExportPdf({
  app,
  id,
  documentSize = "a4",
  orientation = "landscape",
  aspectRatio = 2,
  onExportStart = null,
  onExportFinish = null
}) {
  const exportType = "pdf";
  let vis = null;

  try {
    vis = await app.visualization.get(id);
    const settings = { documentSize, orientation, aspectRatio };

    if (onExportStart) {
      try {
        await onExportStart({ vis, settings, exportType });
      } catch (cbErr) {
        console.error("ExportPdf: onExportStart threw:", cbErr);
      }
    }

    const link = await vis.exportPdf(settings);

    if (onExportFinish) {
      try {
        await onExportFinish({ vis, link, error: null, exportType });
      } catch (cbErr) {
        console.error("ExportPdf: onExportFinish threw:", cbErr);
      }
    } else {
      window.open(link, "_blank");
    }

    return link;
  } catch (error) {
    if (onExportFinish) {
      try {
        await onExportFinish({ vis: null, link: null, error, exportType });
      } catch (cbErr) {
        console.error("ExportPdf: onExportFinish (error path) threw:", cbErr);
      }
    }
    throw error;
  } finally {
    try {
      vis && vis.close();
    } catch (e) {
      // ignore
    }
  }
}

export function ApplyBookmark(app, id) {
  return app.bookmark.apply(id);
}

// To fix issue when bookmark created it can be published after some time
let justCreated = {};
const bmTimeoutMs = process.env.REACT_APP_AUTOSAVE_TIMEOUT_MS || 30000;

function setJustCreated(bid) {
  justCreated[bid] = setTimeout(() => clearJustCreated(bid), bmTimeoutMs);
}

function clearJustCreated(bid) {
  delete justCreated[bid];
}

function isJustCreated(bid) {
  return justCreated[bid];
}

export async function CreateBookmark(app, title, description, sheetId) {
  // const b = await app.bookmark.create(title, description, sheetId);
  const b = await app.model.enigmaModel.createBookmark(
    {
      qInfo: { qType: "bookmark" },
      qMetaDef: {
        title,
        description,
        isExtended: true
      },
      qIncludeVariables: true,
      page: sheetId ? sheetId : "/"
    },
    []
  );

  await b.publish(); // force engine call savePropertiesCaches to store data in db using qrs
  await b.unPublish();
  return b;
}

export function GetBookmark(app, id) {
  return app.model.enigmaModel.getBookmark(id);
}

export function RemoveBookmark(app, id) {
  return app.bookmark.remove(id);
}

function GetFullUserId(user) {
  return user ? `${user.UserDirectory}/${user.getFullUserId()}` : "";
}

export async function PublishBookmark(app, id, user) {
  if (isJustCreated(id)) await sleep(bmTimeoutMs);

  const b = await app.model.enigmaModel.getBookmark(id);

  const userId = GetFullUserId(user);
  const props = await b.getProperties();
  props.qInfo.qType = `bookmark/${userId}`; // Makes published bookmark invisible to other users
  await b.setProperties(props);
  await b.publish();
}

export async function UnpublishBookmark(app, id) {
  const b = await app.model.enigmaModel.getBookmark(id);
  await b.unPublish();

  const props = await b.getProperties();
  props.qInfo.qType = "bookmark";
  await b.setProperties(props);
}

export async function GetBookmarks(app, user) {
  const userId = GetFullUserId(user);
  app.model.enigmaModel.session.config.protocol.delta = false;

  const bookmarks = await app.model.enigmaModel.getBookmarks({
    qOptions: {
      qTypes: ["bookmark", `bookmark/${userId}`], // bookmark's types to load
      qData: {
        creationDate: "/creationDate"
      },
      qIncludePatches: false
    }
  });

  app.model.enigmaModel.session.config.protocol.delta = true;

  return bookmarks.filter(b => {
    return (
      b.qMeta.approved ||
      (b.qMeta.owner.userDirectory === user.UserDirectory &&
        b.qMeta.owner.userId === user.getFullUserId())
    );
  });
}
